import {
  Badge,
  Button,
  Datagrid,
  Menu,
  Icon,
  Input,
  Spacer,
  Stack, PageHeader
} from '@efast_public/fjd-component-library';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserAndRoleManagementModal } from '../../modals/UserAndRoleManagementModal';
import { IUserRoleDto, UserPageResponse } from '../../api/mandant.types';
import { isUserAdministrator, isUserRegistered } from '../../shared/utils/usersAndRoles';
import { useQuery } from '@tanstack/react-query';
import { ApiRoutes } from '../../api/mandant-api';
import DeleteUsersAndRolesManagementDeleteDialog from '../../modals/UsersAndRolesManagementDeleteDialogModal';
import {useNavigate} from "react-router-dom";
import {RouteKey} from "../AppRoutes";

export function UserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedUser, setSelectedUser] = useState<{
    user: IUserRoleDto;
    mode?: 'edit' | 'delete';
  } | null>(null);

  const {
    refetch: loadUsersRoles,
    data: userRolesData,
    isLoading: isValidating
  } = useQuery<UserPageResponse>({
    queryKey: [ApiRoutes.users()]
  });
  const filteredData = useMemo(() => {
    const content = userRolesData?.content ?? [];
    if (!searchValue.trim()) return content;
    const lower = searchValue.toLowerCase();
    return content.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(lower) ||
        user.lastName?.toLowerCase().includes(lower) ||
        user.email?.toLowerCase().includes(lower)
    );
  }, [userRolesData, searchValue]);

  return (
    <Stack spacing="l">
      <div style={{ width: '80rem' }}>
        <Stack spacing="l">
          <PageHeader level={4} heading={t('userPage.title')} text={t('userPage.descriptionText')} />
          <Stack direction="row" spacing="s">
            <Input
              id="userSearch"
              type="search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t('userPage.searchPlaceholder')}
            />
            <Button onClick={() => setIsModalVisible(true)}>
              {t('userPage.addUserButton')}
            </Button>
          </Stack>
        </Stack>
      </div>
      <Datagrid<IUserRoleDto>
        onRowClick={(row: IUserRoleDto) => {
          navigate(`${RouteKey.User}/${row.id}`);
        }}
        columns={[
          {
            id: 'lastName',
            header: t('userPage.table.lastName'),
            accessorKey: 'lastName',
            enableHiding: true,
            size: 150,
            minSize: 2,
            enableSorting: true
          },
          {
            id: 'firstName',
            header: t('userPage.table.firstName'),
            accessorKey: 'firstName',
            enableHiding: true,
            size: 150,
            minSize: 4,
            enableSorting: true,
            enableResizing: true
          },
          {
            id: 'email',
            header: t('userPage.table.email'),
            accessorKey: 'email',
            enableHiding: true,
            size: 300,
            minSize: 4,
            enableSorting: true,
            enableResizing: true
          },
          // {
          //   id: 'groups',
          //   header: t('userPage.table.groups'),
          //   accessorKey: 'groups',
          //   enableHiding: true,
          //   size: 600,
          //   minSize: 4,
          //   enableSorting: false,
          //   sortingFn: 'alphanumeric',
          //   enableResizing: true,
          //   cell: (row) => (
          //     <div>
          //       {row.row.original.orgUnitRoles
          //         .filter((subGroup) => subGroup.roles.length > 0)
          //         .map((role) => (
          //           <div>
          //             {role.name}: {role.roles.at(-1)?.name}
          //           </div>
          //         ))}
          //     </div>
          //   )
          // },
          {
            id: 'isAdmin',
            header: t('userPage.table.isAdmin'),
            accessorKey: 'isAdmin',
            enableHiding: true,
            size: 150,
            minSize: 4,
            enableSorting: false,
            enableResizing: true,
            cell: (row) =>
              isUserAdministrator(row.row.original) && (
                <Icon variant="success" name="checkmark-filled" />
              )
          },
          {
            id: 'status',
            header: t('userPage.table.status'),
            accessorKey: 'status',
            enableHiding: true,
            size: 20,
            minSize: 8,
            enableSorting: false,
            enableResizing: true,
            cell: (row) =>
              isUserRegistered(row.row.original) ? (
                <Badge
                  size="sm"
                  iconBefore="checkmark-filled"
                  variant="success"
                >
                  {t('userPage.table.statusRegistered')}
                </Badge>
              ) : (
                <Badge size="sm" variant="inProgress">
                  {t('userPage.table.statusInvited')}
                </Badge>
              )
          },
          // {
          //   id: 'menu',
          //   header: '',
          //   accessorKey: 'menu',
          //   enableHiding: true,
          //   size: 4,
          //   minSize: 2,
          //   enableSorting: false,
          //   enableResizing: false
            // TODO: I think we should use build a menu button including a menu in the complib v2
            // cell: (row) => (
              // <TableMenuButton>
              //   <Menu.MenuItem
              //     key="dl"
              //     icon="view"
              //     label={t('usersAndRoles.editUserButton')}
              //     onClick={() => {
              //       setSelectedUser({
              //         user: row.row.original,
              //         mode: 'edit'
              //       });
              //     }}
              //   />
              //   <Menu.MenuItem
              //     key="a"
              //     icon="play"
              //     label={t('usersAndRoles.deleteUserButton')}
              //     onClick={() => {
              //       setSelectedUser({
              //         user: row.row.original,
              //         mode: 'delete'
              //       });
              //     }}
              //   />
              // </TableMenuButton>
            // )
          // }
        ]}
        data={filteredData}
        labels={{
          currentPage: t('userPage.table.currentPage'),
          detailButton: t('userPage.table.detailButton'),
          emptyData: t('userPage.table.emptyData'),
          nextPage: t('userPage.table.nextPage'),
          of: t('userPage.table.of'),
          page: t('userPage.table.page'),
          pageSize: t('userPage.table.pageSize'),
          previousPage: t('userPage.table.previousPage'),
          rows: t('userPage.table.rows'),
          tableTitle: '',
          ascBtn: t('userPage.table.ascBtn'),
          descBtn: t('userPage.table.descBtn'),
          hideBtn: t('userPage.table.hideBtn')
        }}
        manualPagination={false}
        isDataLoading={isValidating}
        dataKey="email"
        manualSorting={false}
        areColumnsHideable={false}
        areColumnsResizeable
        tableHeight={540}
        clickableRows
      />
      {isModalVisible && (
        <UserAndRoleManagementModal
          onClose={() => setIsModalVisible(false)}
          isOpen
        />
      )}
      {selectedUser?.mode === 'delete' && (
        <DeleteUsersAndRolesManagementDeleteDialog
          loadUsersRoles={loadUsersRoles}
          onClose={() => setSelectedUser(null)}
          userId={selectedUser.user?.id ?? ''}
          isOpen
        />
      )}
    </Stack>
  );
}
